'use client'

import { PRODUCT_IMAGE_SIZE_MEDIUM } from '@/feature/product/services/product.constants'
import { createDisplayProductScreenIntent } from '@/feature/product/services/product.intents'
import { toProductPage } from '@/feature/product/services/product.routing'
import { useReferencesProductRow } from '@/feature/product/hooks/useReferencesProductRow'
import type { MergedArticle } from '@/feature/product/types/mergedProduct'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Link } from '@/ui/links/link/Link.client'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import { DefinitionList } from '@/ui/lists/definition-list/DefinitionList.client'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ProductAvailabilities_ProductFragment,
  ReferencesProductTable_ArticleFragment,
  ReferencesProductTableRows_PriceAndStockFragment,
} from '@gql-generated/graphql'
import { memo, Suspense, useMemo, type Dispatch, type SetStateAction } from 'react'
import { AddProductToFavoriteButton } from '../../../add-product-to-favorite-button/AddProductToFavoriteButton.client'
import { EquivalentProductsLink } from '../../../equivalent-products/EquivalentProductsLink.client'
import { ProductAvailabilities } from '../../../product-availabilities/ProductAvailabilities.client'
import { ProductAvailabilitiesSkeleton } from '../../../product-availabilities/ProductAvailabilitiesSkeleton'
import { ProductAvailabilityIssueCard } from '../../../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductPrice } from '../../../product-price/ProductPrice.client'
import { ProductTag } from '../../../product-tag/ProductTag.client'
import { ProductTiersPricesInfoCard } from '../../../product-tiers-prices/ProductTiersPricesInfoCard.client'
import { useReferencesProductTableContext } from './ReferencesProductContext'
import { ReferencesProductTableAddToCart } from './ReferencesProductTableAddToCart.client'
import { ReferencesProductTableAddToCartSkeleton } from './ReferencesProductTableAddToCartSkeleton'

export const TECHNICAL_SPECS_LIMIT = 3

export type ReferencesProductRowMobileProps = {
  article: MergedArticle
  isFavorite?: boolean
  setSelectedQuantityAction: Dispatch<SetStateAction<number>>
  selectedQuantity?: number
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ReferencesProductRowMobileComponent({
  article,
  isFavorite,
  setSelectedQuantityAction,
  selectedQuantity,
  pickupPointOfSale,
}: ReferencesProductRowMobileProps) {
  const {
    isAuthenticated,
    isMonoDiscriminant,
    isPublicPriceEnabled,
    isClickAndCollectEnabled,
    cart,
  } = useReferencesProductTableContext()
  const {
    hasPrice,
    displayPrice,
    hasTiersPrices,
    effectiveLoading,
    isAvailabilityLoading,
    isAvailabilityDisplayable,
    isAvailableForSale,
    isProductPriceDisplayable,
  } = useReferencesProductRow({ article })
  const { t } = useTranslation(['referencesProductTable'])

  const shouldDisplayAvailabilities =
    !isAvailabilityLoading && isProductPriceDisplayable && isAvailabilityDisplayable

  const productRoutingIntent = useMemo(
    () => (article.urlKey ? createDisplayProductScreenIntent(article.urlKey) : undefined),
    [article.urlKey],
  )

  return (
    <tr className="bg-white desktop:hidden">
      <td className="relative flex flex-col gap-2 p-2">
        <div className="flex gap-2">
          <Link variant="free" href={toProductPage(article.urlKey)} intent={productRoutingIntent}>
            <CloudflarePictureProduct
              className="max-w-16"
              src={article.mainImage?.set?.[0]?.url}
              alt={article.mainImage?.alt || article.name || ''}
              width={PRODUCT_IMAGE_SIZE_MEDIUM}
            />
          </Link>
          <div className="flex flex-col gap-1">
            {!effectiveLoading && hasPrice && article.price?.tag && (
              <ProductTag tag={article.price.tag} />
            )}

            <div className="flex flex-col gap-0.5">
              {article.brand && (
                <span className="font-bold uppercase text-brand-2-900">{article.brand.name}</span>
              )}
              <Link
                variant="free"
                className="flex flex-col gap-0.5 leading-light"
                href={toProductPage(article.urlKey)}
                intent={productRoutingIntent}
              >
                <span className="line-clamp-3">{article.name}</span>
                <span>{t('referencesProductTable:ref', { ref: article.reference })}</span>
              </Link>
            </div>

            <Suspense fallback={<TextSkeleton className="w-18" size="xl" leading="normal" />}>
              {effectiveLoading ? (
                <TextSkeleton className="w-18" size="xl" leading="normal" />
              ) : !isProductPriceDisplayable ? (
                <ProductAvailabilityIssueCard issue="noPricesAvailable" />
              ) : !hasPrice ? (
                isPublicPriceEnabled && <ProductAvailabilityIssueCard issue="noPricesAvailable" />
              ) : (
                <ProductPrice
                  price={displayPrice.price}
                  priceQuantity={displayPrice.priceQuantity}
                  unitPrice={displayPrice.erpPrice}
                  discount={{
                    price: displayPrice.discountAmount,
                    percentage: displayPrice.discountPercentage,
                    tiersPrices: displayPrice.tiersPrices,
                  }}
                  selectedQuantity={selectedQuantity}
                  packaging={article.stock?.packaging}
                  size="xl"
                />
              )}
            </Suspense>

            <div className="flex flex-col gap-0.5">
              <Suspense
                fallback={
                  <ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: false }]} />
                }
              >
                {isAvailabilityLoading ? (
                  <ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: false }]} />
                ) : (
                  shouldDisplayAvailabilities && (
                    <ProductAvailabilities
                      pickupPointOfSale={pickupPointOfSale}
                      product={article as ProductAvailabilities_ProductFragment}
                      isTitleVisible={false}
                      isHideStore={!isAuthenticated}
                      isClickAndCollectEnabled={isClickAndCollectEnabled}
                      shortMode
                    />
                  )
                )}
              </Suspense>

              {/* No loading skeleton needed: the component returns the login link synchronously for guests */}
              {!isAuthenticated && (
                <Suspense fallback={<TextSkeleton className="w-18" size="md" leading="normal" />}>
                  <ReferencesProductTableAddToCart
                    cart={cart}
                    article={
                      article as ReferencesProductTable_ArticleFragment &
                        ReferencesProductTableRows_PriceAndStockFragment
                    }
                    isMonoDiscriminant={isMonoDiscriminant}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>

        {!!article.additionalTechnicalSpecs?.length && (
          <DefinitionList aria-label={t('referencesProductTable:technicalSpecsListLabel')}>
            <DefinitionList.Column>
              {article.additionalTechnicalSpecs.slice(0, TECHNICAL_SPECS_LIMIT).map(({ label }) => (
                <DefinitionList.Item key={label} isBold>
                  {label}
                </DefinitionList.Item>
              ))}
            </DefinitionList.Column>
            <DefinitionList.Column>
              {article.additionalTechnicalSpecs
                .slice(0, TECHNICAL_SPECS_LIMIT)
                .map(({ label, value }) => (
                  <DefinitionList.Item key={label}>{value}</DefinitionList.Item>
                ))}
            </DefinitionList.Column>
          </DefinitionList>
        )}

        {isAuthenticated &&
          (isAvailableForSale ? (
            <Suspense fallback={<ReferencesProductTableAddToCartSkeleton />}>
              {!effectiveLoading && hasPrice ? (
                <ReferencesProductTableAddToCart
                  cart={cart}
                  article={
                    article as ReferencesProductTable_ArticleFragment &
                      ReferencesProductTableRows_PriceAndStockFragment
                  }
                  isMonoDiscriminant={isMonoDiscriminant}
                  onQuantityChange={setSelectedQuantityAction}
                />
              ) : (
                <ReferencesProductTableAddToCartSkeleton />
              )}
            </Suspense>
          ) : (
            article.isEquivalentProductsLinkVisible &&
            article.urlKey && (
              <div className="mt-1">
                <EquivalentProductsLink slug={article.urlKey} />
              </div>
            )
          ))}

        {isAuthenticated && hasTiersPrices && (
          <ProductTiersPricesInfoCard
            packaging={article.stock?.packaging}
            priceQuantity={displayPrice.priceQuantity}
            tiersPrices={displayPrice.tiersPrices}
            selectedQuantity={selectedQuantity}
            isLoading={effectiveLoading}
          />
        )}

        {isAuthenticated && article.name && (
          <div className="absolute right-0.5 top-0.5">
            <AddProductToFavoriteButton
              product={{
                reference: article.reference,
                name: article.name,
                isFavorite,
              }}
              context={isMonoDiscriminant ? 'mono-discriminant' : 'multi-discriminant'}
            />
          </div>
        )}
      </td>
    </tr>
  )
}

export const ReferencesProductRowMobile = memo(ReferencesProductRowMobileComponent)
